/**
 * Check the sources: a piece in sources_ready, the writer's gathered sources laid out for the
 * editor to untick, add to, and then Write it. Changes save (PATCH) just before the write starts.
 */
import { useEffect, useRef, useState } from 'react'
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type DraftStatus } from '../api/client'
import { describeError } from '../lib/describeError'
import { addPending, alreadyListed, refKey, sourceHref, truncate, writeStillPending, type TitledRef } from '../lib/reviewSources'
import { ArchiveSearch } from '../pages/ReviewSources'
import { usePublicBaseUrl } from '../lib/publicBaseUrls'
import { getPiece, patchSources, setAside, writeIt, type PieceDetail } from './api'
import { countsLine, isDirty, kindLabel, REFERENCE_ONLY_NOTE, reviewDiff, sourceWhere } from './checkSources'
import { chipClass, destinationLabel, stageLabel } from './labels'
import { canPublish, useEditorialMe } from './me'
import { pieceKey } from './piece/usePieceAutosave'
import { btn, btnDanger, focus } from './piece/ui'
import { estimateMinutes, startRefusal } from './start'
import { useEditorialTitle } from './titleContext'
import { useLeaveWarning } from './useLeaveWarning'
import { deskHref, pieceHref } from './links'

const CHIP = 'inline-flex h-6 items-center rounded-full px-2.5 font-ui text-[12px] font-semibold whitespace-nowrap'
const CARD = 'rounded-xl border border-ink-600 bg-ink-800'
const LEAVE_MESSAGE = 'Leave? The source check in progress will be lost.'

export function CheckSourcesPage() {
  const { id } = useParams()
  const n = Number(id)
  if (!Number.isInteger(n) || n <= 0) return <p role="alert" className="font-ui text-[14px] text-radar-red">No such piece.</p>
  return <Check key={n} id={n} />
}

function Check({ id }: { id: number }) {
  const q = useQuery({ queryKey: pieceKey(id), queryFn: () => getPiece(id) })
  const { section } = useEditorialTitle()
  const [leaving, setLeaving] = useState(false)
  if (q.data === undefined) {
    if (q.isError) return <p role="alert" className="font-ui text-[14px] text-radar-red">Could not load the piece. {describeError(q.error)}</p>
    return <p role="status" className="font-ui text-[14px] text-paper-dim">Loading the piece…</p>
  }
  // Anything but sources_ready belongs on the piece page, except while this screen is leaving for the Desk.
  if (q.data.status !== 'sources_ready' && !leaving) return <Navigate to={pieceHref(section.path, id)} replace />
  return <Sources d={q.data} onLeaving={() => setLeaving(true)} />
}

function Sources({ d, onLeaving }: { d: PieceDetail; onLeaving: () => void }) {
  const publicBase = usePublicBaseUrl(d.publication_slug)
  const { section, publicationSlug: slug } = useEditorialTitle()
  const me = useEditorialMe()
  const nav = useNavigate()
  const qc = useQueryClient()
  const desk = deskHref(section.path)
  const [removed, setRemoved] = useState<Set<string>>(new Set())
  const [pending, setPending] = useState<TitledRef[]>([])
  const [error, setError] = useState<string | null>(null)
  const [postedAt, setPostedAt] = useState<number | null>(null)
  const [now, setNow] = useState(0)

  const status = useQuery({
    queryKey: ['section-draft-status', slug],
    queryFn: () => api.get<DraftStatus>(`/api/lab/section-drafts/draft-now/status?publication_slug=${slug}`),
    // Poll while the title's writer is busy, so Write it comes back when it finishes.
    refetchInterval: (query) => (query.state.data?.running ? 10_000 : false),
  })
  useEffect(() => {
    if (postedAt === null) return
    const t = setInterval(() => setNow(Date.now()), 5_000)
    return () => clearInterval(t)
  }, [postedAt])
  const started = writeStillPending(postedAt, status.data, Math.max(now, postedAt ?? 0))

  const originalIds = d.sources.map((s) => s.id)
  const diff = reviewDiff(
    { ids: originalIds, added: [] },
    { ids: originalIds.filter((x) => !removed.has(x)), added: pending.flatMap((p) => ('id' in p.ref && (p.ref.kind === 'member' || p.ref.kind === 'issue') ? [{ kind: p.ref.kind, id: p.ref.id }] : [])) },
  )
  const dirty = isDirty(diff)
  const kept = d.sources.filter((s) => !removed.has(s.id))
  const questions = d.research_questions ?? []
  const quotable = kept.some((s) => !s.reference_only) || diff.include.length > 0

  // Whether this attempt's PATCH went through: only then does the server hold the editor's changes.
  const patched = useRef(false)
  const write = useMutation({
    mutationFn: async () => {
      patched.current = false
      if (dirty) {
        await patchSources(d.id, diff)
        patched.current = true
      }
      const refused = startRefusal(await writeIt(d.id))
      if (refused) throw new Error(refused)
    },
    onSuccess: () => {
      const t = Date.now()
      setPostedAt(t)
      setNow(t)
      onLeaving()
      qc.invalidateQueries({ queryKey: ['editorial-desk', slug] })
      qc.invalidateQueries({ queryKey: ['section-draft-status', slug] })
      nav(desk)
    },
    onError: (e) => {
      setError(describeError(e) || 'Could not start writing.')
      qc.invalidateQueries({ queryKey: ['section-draft-status', slug] })
      // Only a PATCH that went through moves the changes to the server: then show what it holds.
      // A failed PATCH keeps the editor's unticks and additions on screen to try again.
      if (!patched.current) return
      qc.invalidateQueries({ queryKey: pieceKey(d.id) })
      setRemoved(new Set())
      setPending([])
    },
  })
  const aside = useMutation({
    mutationFn: () => setAside(d.id),
    onSuccess: () => {
      onLeaving()
      qc.invalidateQueries({ queryKey: ['editorial-desk', slug] })
      qc.invalidateQueries({ queryKey: ['editorial-pieces'] })
      nav(desk)
    },
    onError: (e) => setError(describeError(e)),
  })
  // Unsaved source changes, or a Write it still on its way, are lost on leaving: ask first
  // (a reload or close, and an in-app link such as the Desk breadcrumb or the editorial tabs).
  const unsaved = dirty && !write.isSuccess
  useLeaveWarning(unsaved || write.isPending, LEAVE_MESSAGE)
  const busy = write.isPending || aside.isPending || started || !!status.data?.running
  const toggle = (sid: string) => setRemoved((s) => {
    const next = new Set(s)
    if (next.has(sid)) next.delete(sid)
    else next.add(sid)
    return next
  })

  const title = d.topic?.label || d.title || 'Untitled piece'
  const format = d.brief.format
  const minutes = estimateMinutes(format === 'legacy' ? 'short' : format)

  return (
    <div className="flex flex-col gap-5">
      <nav aria-label="Breadcrumb" className="font-ui text-[13px] text-paper-dim"><Link to={desk} className="text-amber-glow underline underline-offset-2">Desk</Link> / Check the sources</nav>
      <header className="flex max-w-[720px] flex-col gap-2">
        <span className="flex flex-wrap gap-1.5">
          <span className={chipClass(d.status)}>{stageLabel(d.status)}</span>
          <span className={`${CHIP} bg-ink-600 text-paper`}>{destinationLabel(d.destination, section.label)}</span>
        </span>
        <h1 className="m-0 font-serif text-[34px] font-medium leading-tight text-paper sm:text-[38px]">{title}</h1>
        <p className="m-0 font-ui text-[14px] text-paper-dim">The writer has gathered these. Remove anything off the subject, add what it missed, then press Write it. Nothing has been written yet.</p>
      </header>

      <div className="flex flex-wrap items-start gap-6">
        <div className="flex min-w-0 flex-[3_1_560px] flex-col gap-4">
          {questions.length > 0 && (
            <section className={`${CARD} flex flex-col gap-2.5 px-5 py-4`}>
              <h2 className="m-0 font-ui text-[12px] font-bold uppercase tracking-[0.08em] text-paper-dim">The questions it will research</h2>
              <ol className="m-0 flex flex-col gap-1.5 pl-5 font-serif text-[17px] text-paper">
                {questions.map((rq, i) => <li key={i}>{rq}</li>)}
              </ol>
            </section>
          )}

          <section className="flex flex-col gap-2.5">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="m-0 font-serif text-[24px] font-medium text-paper">{d.sources.length} {d.sources.length === 1 ? 'source' : 'sources'}</h2>
              <span className="font-ui text-[13px] text-paper-dim">{kept.length} will be used{removed.size > 0 ? ` · ${removed.size} removed` : ''}{pending.length > 0 ? ` · ${pending.length} added` : ''}</span>
            </div>
            {d.sources.length === 0 && <p className="m-0 font-ui text-[13px] text-paper-dim">No sources were found. Add some from the archive.</p>}
            {d.sources.map((s) => {
              const keep = !removed.has(s.id)
              const href = sourceHref(publicBase, s)
              const first = s.passages[0]
              const where = sourceWhere(s)
              return (
                <article key={s.id} className={`${CARD} flex flex-wrap items-start gap-x-4 gap-y-3 px-4 py-4 ${keep ? '' : 'opacity-55'}`}>
                  <label className="flex min-h-11 flex-none cursor-pointer items-center gap-2 font-ui text-[14px] font-semibold text-paper">
                    <input type="checkbox" className="h-5 w-5 accent-amber-deep" checked={keep} onChange={() => toggle(s.id)} /> Use
                  </label>
                  <div className="flex min-w-0 flex-[1_1_320px] flex-col gap-1.5">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className={`${CHIP} ${s.kind === 'web' ? 'bg-radar-cyan/15 text-radar-cyan' : 'bg-ink-600 text-paper'}`}>{kindLabel(s.kind, section.label)}</span>
                      {href
                        ? <a className={`font-ui text-[14px] font-semibold text-paper underline underline-offset-2 ${keep ? '' : 'line-through'} ${focus}`} href={href} target="_blank" rel="noreferrer">{s.title}</a>
                        : <span className={`font-ui text-[14px] font-semibold text-paper ${keep ? '' : 'line-through'}`}>{s.title}</span>}
                      {where && <span className="font-ui text-[12px] text-paper-dim">{where}</span>}
                    </span>
                    {first && <span className="font-serif text-[15px] leading-snug text-paper-dim">“{truncate(first, 300)}”</span>}
                    {s.reference_only && <span className="font-ui text-[12px] text-paper-dim">{REFERENCE_ONLY_NOTE}</span>}
                    {!keep && <span className="font-ui text-[12px] text-paper-dim">You removed this one.</span>}
                  </div>
                </article>
              )
            })}
            {pending.map((p) => (
              <article key={refKey(p.ref)} className={`${CARD} flex flex-wrap items-center gap-3 border-radar-green px-4 py-3`}>
                <span className={`${CHIP} bg-radar-green/15 text-radar-green`}>Added</span>
                <span className="min-w-0 flex-1 font-ui text-[14px] font-semibold text-paper">{p.title}</span>
                <button type="button" className={btn} onClick={() => setPending((xs) => xs.filter((x) => refKey(x.ref) !== refKey(p.ref)))}>Do not add</button>
              </article>
            ))}
          </section>
        </div>

        <aside className="sticky top-6 flex min-w-0 flex-[1_1_300px] flex-col gap-3.5">
          <section className={`${CARD} flex flex-col gap-2.5 p-4`}>
            <h2 className="m-0 font-ui text-[12px] font-bold uppercase tracking-[0.08em] text-paper-dim">Add from the archive</h2>
            <ArchiveSearch publicationSlug={slug} useLabel="Add"
                           listed={(h) => alreadyListed(h, originalIds, removed, pending)}
                           onUse={(h) => setPending((p) => addPending(p, h))} />
          </section>
          <section className="flex flex-col gap-2.5 rounded-[14px] bg-paper p-5 text-ink-900">
            <span className="font-ui text-[13px] text-ink-500">{countsLine(kept.length + pending.length, questions.length, format)}</span>
            {d.error && <p role="alert" className="m-0 font-ui text-[13px] font-semibold text-amber-deep">The last attempt failed: {d.error}</p>}
            <button type="button" disabled={busy || !quotable} onClick={() => { setError(null); write.mutate() }}
                    className={`inline-flex min-h-12 items-center justify-center rounded-lg border border-amber-deep bg-amber-deep px-4 font-ui text-[15px] font-semibold text-paper hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50 ${focus}`}>
              {write.isPending ? 'Starting…' : started || status.data?.running ? 'A draft is being written…' : 'Write it'}
            </button>
            {!quotable && <span className="text-center font-ui text-[12px] text-ink-500">Keep at least one source to write from.</span>}
            {error && <p role="alert" className="m-0 font-ui text-[13px] font-semibold text-amber-deep">{error}</p>}
            <span className="text-center font-ui text-[12px] text-ink-500">Your changes are saved when you press Write it. About {minutes} minutes.</span>
          </section>
          {canPublish(me.data) && (
            <button type="button" className={btnDanger} disabled={busy}
                    onClick={() => { if (window.confirm('Set this piece aside? It frees the subject for a new piece.')) { setError(null); aside.mutate() } }}>
              Set this piece aside
            </button>
          )}
        </aside>
      </div>
    </div>
  )
}
